import { Box, BoxProps, Button, Card, Dialog, DialogContent, DialogTitle, Drawer, FormGroup, FormHelperText, Grid, styled, TextField, Typography } from "@mui/material";
import {
    DataGridPro,
    GridColumns,
    GridRowModelUpdate,
    useGridApiContext,
    GridRowParams,
} from '@mui/x-data-grid-pro';
import Close from 'mdi-material-ui/Close'
import React from "react";
import { ApiResponseType } from "../../../types";
import { client } from "../../../utils/axios-client";
import { CadenceTemplate, CadenceTemplateVariant, Playbook, PlaybookLibrary, renamePlaybookTemplate, saveTemplate, saveTemplateVariant, toggleTemplateStatus } from "../PlaybookStore";
// ** Icons Imports
import PencilOutline from 'mdi-material-ui/PencilOutline'
import IconButton from '@mui/material/IconButton'
import CustomChip from 'src/materio/@core/components/mui/chip'
import ContentEditor from "../../common/ContentEditor";
import _ from "lodash";
import SourceIcon from '@mui/icons-material/Source';
import TemaplateWithVariantRow from "./TemapletWithVariantsRow";
import { PlaybookStore } from "../PlaybookStore";
import AddIcon from '@mui/icons-material/Add'
import NameDialog from "../NameDialog";
import NameDialogWithTemplate from "../NameDialogWithTemplate";
import VariantFilters from "./VariantFilters";

const Header = styled(Box)<BoxProps>(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(3, 4),
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default
}))

interface CellType {
    row: CadenceTemplate
}

const defaultColumns = [
    {
        flex: 0.65,
        field: 'name',
        minWidth: 240,
        headerName: 'Name',
        renderCell: ({ row }: CellType) => <Typography>{row.name}</Typography>
    },
    {
        flex: 0.25,
        field: 'templateType',
        minWidth: 240,
        headerName: 'Template Type',
        renderCell: ({ row }: CellType) => <Typography>{row.templateType}</Typography>
    }
]



type Props = {
    open: boolean,
    onClose: () => void,
}

const EditCadenceDrawer = ({ open, onClose }: Props) => {
    /*const [selectedRecord, setSelectedRecord] = React.useState(null as null | CadenceTemplate);*/
    const [filter, setSearchValue] = React.useState<string>('')
    const [pageSize, setPageSize] = React.useState<number>(4)
    const [rows, setRows] = React.useState<CadenceTemplate[]>([]);
    const [renameDialogOpen, setRenameDialogOpen] = React.useState<boolean>(false)
    const playbook = PlaybookStore.useState(s => s.selectedPlaybook);
    const [addTemplateDialogOpen, setAddTemplateDialogOpen] = React.useState<boolean>(false)
    const selectedTemplate = PlaybookStore.useState(s => s.selectedTemplate);
    const selectedTemplateVariant = PlaybookStore.useState(s => s.selectedTemplateVariant);
    const [contentEditorOpen, setContentEditorOpen] = React.useState(false);
    const [addVariantDialogOpen, setAddVariantDialogOpen] = React.useState(false);
    const [editVariantDialogOpen, setEditVariantDialogOpen] = React.useState(false);
    const [showVariantFiltersOpen, setShowVariantFiltersOpen] = React.useState(false);

    const getDetailPanelContent = React.useCallback(
        ({ row }: GridRowParams) => <TemaplateWithVariantRow parentRow={row} onEditVariant={onEditVariant} filter={filter} onShowFilters={onShowFilters} disabled={editVariantDialogOpen || contentEditorOpen || showVariantFiltersOpen} />,
        [editVariantDialogOpen, contentEditorOpen, showVariantFiltersOpen],
    );

    const getDetailPanelHeight = React.useCallback(() => 240, []);


    React.useEffect(() => {
        if (playbook === null) {
            setRows([]);
        }
        else if (!_.isEmpty(filter)) {
            setRows(playbook.templates.filter(row => row.name.toLowerCase().includes(filter.toLowerCase())));
        } else {
            setRows(playbook.templates);
        }

    }, [playbook, filter])


    const onShowFilters = (row: CadenceTemplateVariant) => {
        PlaybookStore.update(s => {
            s.selectedTemplateVariant = row;
        })

        setShowVariantFiltersOpen(true);
    }

    const onAddVariant = async (row: CadenceTemplate) => {
        PlaybookStore.update(s => {
            s.selectedTemplate = row;
        })

        setAddVariantDialogOpen(true);
    }

    const onEditVariant = (row: CadenceTemplateVariant) => {
        PlaybookStore.update(s => {
            s.selectedTemplateVariant = row;
        })

        setEditVariantDialogOpen(true);
    }

    const onSaveContent = async (html: string) => {
        if (!selectedTemplate) return;

        const newLibrary = { ...selectedTemplate, templateBody: html, subject: '' };
        const response = await client.post<ApiResponseType>('/api/playbooks/templates', newLibrary);

        if (response.data.succeeded) {
            PlaybookStore.update(s => {
                s.selectedTemplate = null;
            })
        } else {
            alert('error saving content');
        }
    }

    const onSaveVariantContent = async (html: string) => {
        if (!selectedTemplateVariant) return;

        const newLibrary = { ...selectedTemplateVariant, templateBody: html };
        const response = await client.post<ApiResponseType>('/api/playbooks/templates', newLibrary);

        if (response.data.succeeded) {
            PlaybookStore.update(s => {
                s.selectedTemplateVariant = null;
            })

            setEditVariantDialogOpen(false);
        } else {
            alert('error saving content');
        }
    }

    const handleFilter = React.useCallback((val: string) => {
        setSearchValue(val)
    }, [])

    const onLibrarySelect = async (id: string) => {
        const response = await client.get('/api/playbooks/templates', { params: { id: id } });

        PlaybookStore.update(s => {
            s.selectedTemplate = response.data;
        })
        setContentEditorOpen(true);

        /*setSelectedRecord(response.data);*/
    }

    const handleStartRename = (row: CadenceTemplate) => {

        PlaybookStore.update((s, o) => {
            s.selectedTemplate = { ...row, templateBody: o.selectedTemplate?.templateBody ?? '' }
        })

        setRenameDialogOpen(true);
    }

    const columns = [
        ...defaultColumns,        
        {
            flex: 0.15,
            minWidth: 115,
            sortable: false,
            field: 'actions',
            headerName: 'Actions',
            renderCell: ({ row }: CellType) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => handleStartRename(row)} title="Rename Template" disabled={editVariantDialogOpen || contentEditorOpen || showVariantFiltersOpen}>
                        <PencilOutline fontSize='small' />
                    </IconButton>
                    <IconButton onClick={() => onLibrarySelect(row.id)} title="Edit Content" disabled={editVariantDialogOpen || contentEditorOpen || showVariantFiltersOpen}>
                        <SourceIcon fontSize='small' />
                    </IconButton>
                    <IconButton onClick={() => onAddVariant(row)} title="Add Variant" disabled={editVariantDialogOpen || contentEditorOpen || showVariantFiltersOpen}>
                        <AddIcon fontSize='small' />
                    </IconButton>
                </Box>
            )
        }
    ]

    const onSaveTemplate = async (row: CadenceTemplate) => {
        if (playbook === null) throw 'Playbook not selected';
        const response = await saveTemplate(row, playbook, filter);


        if (response.succeeded) {
            setAddTemplateDialogOpen(false)
            setContentEditorOpen(false);
        }

        return response;
    }

    const onSaveVariant = async (row: CadenceTemplateVariant) => {
        if (selectedTemplate === null || playbook === null) throw 'Playbook not selected';

        const templateWithContent = await client.get('/api/playbooks/templates', { params: { id: selectedTemplate.id } });

        const response = await saveTemplateVariant({ ...row, templateBody: templateWithContent.data.templateBody, templateTypeId: selectedTemplate.templateTypeId, subject: templateWithContent.data.subject }, playbook, selectedTemplate, filter);


        if (response.succeeded) {
            setAddTemplateDialogOpen(false)
            setContentEditorOpen(false);
        }

        return response;
    }

    return (<Drawer
        open={open}
        anchor='right'
        variant='temporary'
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { width: { xs: 300, sm: 800, md: 1000 } } }}
    >
        <Header>
            <Typography variant='h6'>Playbook Cadence Templates</Typography>

            <Close fontSize='small' onClick={onClose} sx={{ cursor: 'pointer' }} />
        </Header>

        <Grid container spacing={2} sx={{ padding: 5 }}>
            <Grid item xs={12}>
                <Card sx={{ maxHeight: '40vh', height: '40vh' }}>
                    <Box
                        sx={{ p: 5, pb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                        <TextField
                            size='small'
                            value={filter}
                            sx={{ mr: 4, mb: 2 }}
                            placeholder='Search Templates'
                            onChange={e => handleFilter(e.target.value)}
                        />
                        <Button sx={{ mb: 2 }} variant='contained' onClick={() => setAddTemplateDialogOpen(true)} startIcon={<AddIcon />} size="small">
                            Add Temaplate
                        </Button>
                    </Box>
                    <DataGridPro
                        autoHeight
                        rows={rows}
                        columns={columns}
                        pageSize={pageSize}
                        disableSelectionOnClick
                        rowsPerPageOptions={[10, 25, 50]}
                        rowThreshold={0}
                        onPageSizeChange={(newPageSize: number) => setPageSize(newPageSize)}
                        getDetailPanelContent={getDetailPanelContent}
                        getDetailPanelHeight={getDetailPanelHeight}
                    />
                </Card>
                <Grid item xs={12}>
                    {selectedTemplate && contentEditorOpen && <ContentEditor onClose={() => setContentEditorOpen(false)} html={selectedTemplate.templateBody} onSave={onSaveContent} />}
                    {selectedTemplateVariant && editVariantDialogOpen && <ContentEditor onClose={() => setEditVariantDialogOpen(false)} html={selectedTemplateVariant.templateBody} onSave={onSaveVariantContent} />}
                </Grid>
            </Grid>
        </Grid>

        {selectedTemplate && <NameDialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} onSubmit={(data: CadenceTemplate) => renamePlaybookTemplate(data, filter)} title="Rename Template" subTitle="Please enter a name for the template" fieldLabel="Playbook Temaplte" confirmButtonLabel="Rename" record={selectedTemplate} />}
        <NameDialogWithTemplate open={addTemplateDialogOpen} onClose={() => setAddTemplateDialogOpen(false)} onSubmit={onSaveTemplate} title="Create Template" subTitle="Please enter a name for the template" fieldLabel="Template Name" confirmButtonLabel="Create Template" record={{ name: '' } as CadenceTemplate} />
        <NameDialog open={addVariantDialogOpen} onClose={() => setAddVariantDialogOpen(false)} onSubmit={onSaveVariant} title="Create Variant" subTitle="Please enter a name for the template variant" fieldLabel="Template Variant Name" confirmButtonLabel="Create Template Variant" record={{ name: '' } as CadenceTemplateVariant} />
        {showVariantFiltersOpen && <VariantFilters open={showVariantFiltersOpen} onClose={() => setShowVariantFiltersOpen(false)} />}
    </Drawer>
    )
}

export { EditCadenceDrawer };
export default EditCadenceDrawer;